import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

// Took from timbreuse project
/**
 * Formats a given time in seconds into a string in "HH:mm" format.
 * If the total hours (including days converted to hours) is 100 or more,
 * the hours part will be padded to 3 digits; otherwise, it will be padded to 2 digits.
 * The function always returns hours and minutes, omitting seconds.
 *
 * @param time - The time in seconds to format. Can be negative; the absolute value is used.
 * @returns A string representing the formatted time in "HH:mm" format.
 */
function formatTime(time: number): string {
  // console.log("time: ", time)
  time = Math.abs(time);
  const duration = dayjs.duration(time, 'seconds');
  const hoursDigits = duration.days() * 24 + duration.hours() >= 99 ? 3 : 2;
  const hours = String(duration.hours() + duration.days() * 24).padStart(hoursDigits, '0');
  const minutes = String(duration.minutes()).padStart(2, '0');
  // const seconds = String(duration.seconds()).padStart(2, '0');

  // return `${hours}:${minutes}:${seconds}`
  return `${hours}:${minutes}`;
}

function formatBalance(balance: number) {
  const sign = balance < 0 ? '-' : '+';
  return sign + formatTime(balance);
}

/**
 * Convert UTC date string to user's local timezone
 * @param dateString Date string in UTC format
 * @returns Date string in local timezone
 */
function toLocalTime(dateString: string): Date {
  if (!dateString) return new Date();
  return dayjs.utc(dateString).local().toDate();
}

/**
 * Convert UTC date string to restaurant timezone
 * @param dateString Date string in UTC format
 * @param restaurantTimezone Restaurant's timezone (e.g., 'Europe/Paris')
 * @returns Date string in restaurant timezone
 */
function toRestaurantTime(dateString: string, restaurantTimezone?: string): Date {
  if (!dateString) return new Date();
  if (!restaurantTimezone) return dayjs.utc(dateString).local().toDate();
  return dayjs.utc(dateString).tz(restaurantTimezone).toDate();
}

/**
 * Convert local date to UTC string format for API requests
 * @param date Date object or string in local timezone
 * @returns ISO string in UTC timezone
 */
function toUTCString(date: Date | string, restaurantTimezone?: string): string {
  if (!restaurantTimezone) return dayjs(date).utc().format();
  return dayjs.tz(date, restaurantTimezone).utc().format();
}

/**
 * Get current time in UTC format for API requests
 * @returns ISO string in UTC timezone
 */
function getCurrentUTCTime(): string {
  return dayjs().utc().format();
}

/**
 * Get current time in restaurant timezone
 * @param restaurantTimezone Restaurant's timezone
 * @returns Current time in restaurant timezone
 */
function getCurrentRestaurantTime(restaurantTimezone?: string): dayjs.Dayjs {
  if (!restaurantTimezone) return dayjs();
  return dayjs().tz(restaurantTimezone);
}

/**
 * Format a date object or string for display in local timezone
 * @param date Date object or string
 * @param format Format string (defaults to 'YYYY-MM-DD HH:mm:ss')
 * @returns Formatted date string in local timezone
 */
function formatLocalDateTime(date: Date | string, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
  return dayjs(date).format(format);
}

/**
 * Format a date object or string for display in restaurant timezone
 * @param date Date object or string (UTC)
 * @param format Format string (defaults to 'YYYY-MM-DD HH:mm:ss')
 * @param restaurantTimezone Restaurant's timezone
 * @returns Formatted date string in restaurant timezone
 */
function formatRestaurantDateTime(date: Date | string, format: string = 'YYYY-MM-DD HH:mm:ss', restaurantTimezone?: string): string {
  if (!restaurantTimezone) return dayjs(date).format(format);
  return dayjs.utc(date).tz(restaurantTimezone).format(format);
}

/**
 * Calculate time difference in seconds between two date strings
 * @param startTimeString UTC start time string
 * @param endTimeString UTC end time string (defaults to now if not provided)
 * @returns Time difference in seconds
 */
// function getTimeDifferenceInSeconds(startTimeString: string, endTimeString?: string): number {
//   const startTime = dayjs.utc(startTimeString).local();
//   const endTime = endTimeString ? dayjs.utc(endTimeString).local() : dayjs().local();

//   console.log('startTime: ', startTime);
//   console.log('endTime: ', endTime);

//   return endTime.diff(startTime, 'second');
// }

/**
 * Calculate time difference in seconds between two date strings in restaurant timezone
 * @param startTimeString UTC start time string
 * @param endTimeString UTC end time string (defaults to now if not provided)
 * @param restaurantTimezone Restaurant's timezone
 * @returns Time difference in seconds
 */
function getTimeDifferenceInSeconds(startTimeString: string, endTimeString?: string, restaurantTimezone?: string): number {
  const startTime = dayjs.utc(startTimeString);
  const endTime = endTimeString ? dayjs.utc(endTimeString) : restaurantTimezone ? dayjs().tz(restaurantTimezone).utc() : dayjs().utc();

  return endTime.diff(startTime, 'second');
}

/**
 * Format date to YYYY-MM-DD format in local timezone
 * @param date Date object or string (defaults to current date)
 * @returns Date string in YYYY-MM-DD format
 */
function formatLocalDate(date?: Date | string): string {
  if (!date) {
    return dayjs().format('YYYY-MM-DD');
  }
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Format date to YYYY-MM-DD format in restaurant timezone
 * @param date Date object or string (defaults to current date in restaurant timezone)
 * @param restaurantTimezone Restaurant's timezone
 * @returns Date string in YYYY-MM-DD format
 */
function formatRestaurantDate(date?: Date | string, restaurantTimezone?: string): string {
  if (!date) {
    return getCurrentRestaurantTime(restaurantTimezone).format('YYYY-MM-DD');
  }
  if (!restaurantTimezone) return dayjs(date).format('YYYY-MM-DD');
  return dayjs.utc(date).tz(restaurantTimezone).format('YYYY-MM-DD');
}

/**
 * Format a time range from start and end time strings
 * @param startTime Time string in HH:MM format
 * @param endTime Time string in HH:MM format
 * @returns Formatted time range string
 */
function formatTimeRange(startTime?: string, endTime?: string, t?: (key: string) => string): string {
  const format = (time: string | undefined) => (time ? time.substring(0, 5) : undefined);

  const formattedStartTime = format(startTime);
  const formattedEndTime = format(endTime);

  const fromText = t ? t('common.time.start') : 'start';
  const untilText = t ? t('common.time.end') : 'end';

  if (!formattedStartTime && !formattedEndTime) return '';
  if (!formattedStartTime) return `${untilText} ${formattedEndTime}`;
  if (!formattedEndTime) return `${fromText} ${formattedStartTime}`;
  return `${formattedStartTime} - ${formattedEndTime}`;
}

/**
 * Format a date string or Date object for display with date and time
 * @param date Date object or string (UTC)
 * @param format Format string (defaults to 'DD/MM/YYYY HH:mm')
 * @returns Formatted date string in local timezone
 */
function formatDateTime(date: Date | string, format: string = 'DD.MM.YYYY HH:mm'): string {
  if (!date) return '';
  return dayjs(date).format(format);
}

/**
 * Format a single date for display
 * @param date Date object or string
 * @param format Format string (defaults to 'DD.MM.YYYY')
 * @returns Formatted date string
 */
function formatDisplayDate(date: Date | string, format: string = 'DD.MM.YYYY'): string {
  if (!date) return '';
  return dayjs(date).format(format);
}

/**
 * Format an array of date strings into a date range for display
 * @param dates Array of date strings (YYYY-MM-DD format)
 * @param format Format string for individual dates (defaults to 'DD.MM.YYYY')
 * @returns Formatted date range string (single date or "start - end")
 */
function formatDateRange(dates: string[], format: string = 'DD.MM.YYYY'): string {
  if (dates.length === 1) {
    return dayjs(dates[0]).format(format);
  } else if (dates.length >= 2) {
    return `${dayjs(dates[0]).format(format)} - ${dayjs(dates[dates.length - 1]).format(format)}`;
  }
  return '';
}

export {
  formatBalance,
  formatDateRange,
  formatDateTime,
  formatDisplayDate,
  formatLocalDate,
  formatLocalDateTime,
  formatRestaurantDate,
  formatRestaurantDateTime,
  formatTime,
  formatTimeRange,
  getCurrentRestaurantTime,
  getCurrentUTCTime,
  getTimeDifferenceInSeconds,
  toLocalTime,
  toRestaurantTime,
  toUTCString,
};
